<template>
    <div class="min-w-0" aria-live="polite" @click.capture="handleFeedNavigation">
        <div v-if="newPostsAvailable"
            class="pointer-events-none sticky top-4 z-20 mb-4 flex justify-center"
            data-feed-new-posts-container>
            <button type="button"
                class="foxy-btn-secondary pointer-events-auto px-5 py-2.5 text-center font-semibold shadow-lg"
                data-feed-new-posts @click="startFreshForNewPosts">
                Є нові дописи
            </button>
        </div>

        <p v-if="restoreNotice" class="mb-4 rounded-xl border border-foxy-border bg-foxy-elevated/70 px-4 py-3 text-sm text-foxy-text-secondary"
            data-feed-restore-notice role="status">
            Не вдалося відновити попереднє місце у стрічці.
        </p>

        <div v-if="restoring" class="space-y-4" data-feed-state="restoring" role="status">
            <p class="foxy-panel p-6 text-center text-sm text-foxy-text-secondary">Відновлюємо ваше місце у стрічці…</p>
        </div>

        <div v-else-if="loading && items.length === 0" class="space-y-4" data-feed-state="loading">
            <div v-for="n in 5" :key="`skeleton-${n}`" class="foxy-card p-6">
                <div class="animate-pulse">
                    <div class="mb-4 flex items-center gap-3">
                        <div class="user-avatar user-avatar--md bg-foxy-elevated"></div>
                        <div class="flex-1 space-y-2">
                            <div class="h-4 w-1/3 rounded bg-foxy-elevated"></div>
                            <div class="h-3 w-1/4 rounded bg-foxy-elevated"></div>
                        </div>
                    </div>
                    <div class="mb-3 h-6 w-3/4 rounded bg-foxy-elevated"></div>
                    <div class="h-4 w-full rounded bg-foxy-elevated"></div>
                </div>
            </div>
        </div>

        <div v-else-if="error && items.length === 0" class="foxy-panel p-12 text-center" data-feed-state="error" role="alert">
            <i class="fas fa-exclamation-circle text-3xl text-foxy-error" aria-hidden="true"></i>
            <h2 class="mt-3 text-xl font-bold text-foxy-text">
                {{ invalidCursor ? 'Не вдалося продовжити стрічку' : 'Не вдалося завантажити дописи' }}
            </h2>
            <p class="mx-auto mt-2 max-w-lg text-foxy-text-secondary">
                {{ invalidCursor
                    ? 'Посилання продовження застаріло або пошкоджене. Почніть цю стрічку спочатку.'
                    : 'Перевірте з’єднання та спробуйте ще раз.' }}
            </p>
            <button type="button" class="foxy-btn-primary mt-5 px-5 py-2.5"
                @click="invalidCursor ? restartFeed() : retryRequest()">
                {{ invalidCursor ? 'Почати спочатку' : 'Спробувати ще раз' }}
            </button>
        </div>

        <div v-else-if="items.length === 0 && !hasMore" class="foxy-panel p-12 text-center"
            data-feed-state="empty">
            <i class="fas fa-feather-alt text-4xl text-foxy-action" aria-hidden="true"></i>
            <h2 class="mt-4 text-xl font-bold text-foxy-text">{{ emptyTitle }}</h2>
            <p class="mt-2 text-foxy-text-secondary">{{ emptyDescription }}</p>
        </div>

        <template v-else>
            <transition-group v-if="items.length" name="feed-card" tag="div" class="space-y-4">
                <feed-card v-for="item in items" :key="item.key" :item="item" />
            </transition-group>

            <div v-if="skippedCount > 0" class="mt-4 rounded-xl border border-foxy-border bg-foxy-elevated/70 px-4 py-3 text-sm text-foxy-text-secondary">
                {{ skippedCount }} {{ skippedLabel }} пропущено, бо вони більше не доступні.
            </div>

            <div v-if="error" class="foxy-panel mt-6 p-6 text-center" data-feed-state="continuation-error" role="alert">
                <h2 class="text-lg font-bold text-foxy-text">
                    {{ invalidCursor ? 'Не вдалося продовжити стрічку' : 'Не вдалося завантажити наступні дописи' }}
                </h2>
                <p class="mx-auto mt-2 max-w-lg text-sm text-foxy-text-secondary">
                    Уже завантажені дописи залишаються доступними.
                    {{ invalidCursor
                        ? 'Посилання продовження застаріло або пошкоджене.'
                        : 'Перевірте з’єднання та спробуйте ще раз.' }}
                </p>
                <button type="button" class="foxy-btn-secondary mt-4 px-5 py-2.5"
                    @click="invalidCursor ? restartFeed() : retryRequest()">
                    {{ invalidCursor ? 'Почати спочатку' : 'Спробувати ще раз' }}
                </button>
            </div>

            <div v-else-if="hasMore" class="mt-6 flex flex-col items-center gap-3"
                :data-feed-state="items.length ? 'more' : 'sparse'">
                <p v-if="items.length === 0" class="text-center text-sm text-foxy-text-secondary">
                    У цьому фрагменті немає доступних карток, але стрічка має продовження.
                </p>
                <button type="button" class="foxy-btn-secondary px-6 py-3"
                    :disabled="loading" @click="loadMore">
                    <i v-if="loading" class="fas fa-spinner fa-spin" aria-hidden="true"></i>
                    {{ loading ? 'Завантажуємо…' : (items.length ? 'Завантажити ще' : 'Продовжити стрічку') }}
                </button>
            </div>

            <div v-else-if="items.length" class="mt-8 border-b-2 border-foxy-action pb-3 text-center text-sm font-medium text-foxy-text-secondary"
                data-feed-state="end">
                Ви переглянули всі доступні дописи.
            </div>
        </template>
    </div>
</template>

<script>
import axios from 'axios';
import FeedCard from './FeedCard.vue';
import feedSession from '../utils/feedSessionContinuity';

export default {
    name: 'UnifiedContentList',
    components: { FeedCard },
    props: {
        initialPage: {
            type: Object,
            required: true,
        },
        activeFilter: {
            type: String,
            default: 'all',
        },
        endpoint: {
            type: String,
            default: '/discussions',
        },
        restoreEndpoint: {
            type: String,
            default: '/discussions/session/restore',
        },
        newPostsEndpoint: {
            type: String,
            default: '/discussions/session/new',
        },
    },
    data() {
        return {
            items: Array.isArray(this.initialPage.items) ? [...this.initialPage.items] : [],
            nextCursor: this.initialPage.next_cursor || null,
            highWaterToken: this.initialPage.high_water_token || null,
            hasMore: Boolean(this.initialPage.has_more),
            skippedCount: Number(this.initialPage.skipped_count || 0),
            currentPageFilter: this.initialPage.canonical_filter || this.activeFilter,
            loading: false,
            loadingGeneration: null,
            error: null,
            invalidCursor: false,
            lastFailedCursor: null,
            lastFailedReplace: false,
            requestGeneration: 0,
            sessionId: null,
            viewer: 'guest',
            restoring: false,
            restoreNotice: false,
            newPostsAvailable: false,
            newPostsRequest: null,
            lastNewPostCheckAt: 0,
            continuityReady: false,
            continuityDisposed: false,
        };
    },
    computed: {
        emptyTitle() {
            return {
                all: 'У стрічці ще немає дописів',
                discussion: 'Дискусій ще немає',
                review: 'Рецензій ще немає',
                quote: 'Цитат ще немає',
                fact: 'Фактів ще немає',
                collection: 'Добірок ще немає',
            }[this.activeFilter] || 'Дописи не знайдено';
        },
        emptyDescription() {
            return {
                all: 'У стрічці поки немає дописів.',
                discussion: 'Поки немає дискусій.',
                review: 'Поки немає рецензій і відгуків.',
                quote: 'Поки ніхто не поділився цитатами.',
                fact: 'У стрічці поки немає фактів.',
                collection: 'У стрічці поки немає добірок.',
            }[this.activeFilter] || 'Поки немає дописів.';
        },
        skippedLabel() {
            const value = this.skippedCount % 10;
            const hundred = this.skippedCount % 100;
            if (value === 1 && hundred !== 11) return 'допис';
            if (value >= 2 && value <= 4 && (hundred < 12 || hundred > 14)) return 'дописи';
            return 'дописів';
        },
    },
    watch: {
        activeFilter(nextFilter) {
            if (nextFilter !== this.currentPageFilter) {
                this.resetForFilter(nextFilter);
            }
        },
    },
    mounted() {
        this.initializeContinuity();
        window.addEventListener('pagehide', this.handlePageHide);
        window.addEventListener('pageshow', this.handlePageShow);
        window.addEventListener('focus', this.handleFocus);
        document.addEventListener('visibilitychange', this.handleVisibilityChange);
    },
    beforeDestroy() {
        this.continuityDisposed = true;
        window.removeEventListener('pagehide', this.handlePageHide);
        window.removeEventListener('pageshow', this.handlePageShow);
        window.removeEventListener('focus', this.handleFocus);
        document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    },
    methods: {
        initializeContinuity() {
            this.viewer = feedSession.viewerMarker();
            feedSession.synchronizeViewerContext(this.viewer);
            const existingId = feedSession.currentHistorySession();
            const stored = existingId ? feedSession.loadRecord(existingId) : null;
            const canRestore = stored
                && stored.filter === this.activeFilter
                && stored.viewer_marker === this.viewer;

            if (canRestore) {
                this.sessionId = existingId;
                this.lastNewPostCheckAt = stored.last_new_post_check_at;
                this.continuityReady = true;
                this.restoreFromRecord(stored);
                return;
            }

            if (existingId) feedSession.safeRemove(existingId);
            this.sessionId = feedSession.createHistorySession();
            this.continuityReady = true;
            this.persistSession();
        },
        replaceSessionMetadata() {
            if (this.sessionId) feedSession.safeRemove(this.sessionId);
            this.sessionId = feedSession.createHistorySession();
            this.lastNewPostCheckAt = 0;
            this.newPostsAvailable = false;
        },
        currentRecord() {
            const page = feedSession.recordForPage({
                filter: this.currentPageFilter,
                viewer: this.viewer,
                highWaterToken: this.highWaterToken,
                nextCursor: this.nextCursor,
                hasMore: this.hasMore,
                skippedCount: this.skippedCount,
                items: this.items,
            });
            if (!page) return null;

            const previous = this.sessionId ? feedSession.loadRecord(this.sessionId) : null;
            return feedSession.normalizeRecord({
                ...page,
                anchor_key: previous ? previous.anchor_key : null,
                anchor_offset: previous ? previous.anchor_offset : null,
                scroll_y: previous ? previous.scroll_y : 0,
                last_new_post_check_at: this.lastNewPostCheckAt,
            });
        },
        persistSession(capturePosition = false) {
            if (!this.continuityReady || !this.sessionId) return false;
            let record = this.currentRecord();
            if (!record) return false;
            if (capturePosition) record = feedSession.captureAnchor(record);
            return feedSession.saveRecord(this.sessionId, record);
        },
        async restoreFromRecord(record) {
            const generation = ++this.requestGeneration;
            this.restoring = true;
            this.items = [];
            this.error = null;
            this.invalidCursor = false;

            try {
                const response = await axios.post(this.restoreEndpoint, {
                    v: feedSession.VERSION,
                    filter: record.filter,
                    viewer_marker: record.viewer_marker,
                    high_water_token: record.high_water_token,
                    next_cursor: record.next_cursor,
                    descriptors: record.descriptors,
                    skipped_count: record.skipped_count,
                }, { headers: { Accept: 'application/json' } });
                if (generation !== this.requestGeneration || record.filter !== this.activeFilter) return false;

                const page = response.data;
                if (page.canonical_filter !== record.filter || !Array.isArray(page.items)) {
                    throw new Error('Unexpected Feed restore response');
                }
                this.applyPage(page, true, true);
                this.restoreNotice = false;
                this.persistSession();
                this.restoring = false;
                await this.$nextTick();
                requestAnimationFrame(() => {
                    feedSession.restoreScroll(record);
                    window.setTimeout(() => feedSession.restoreScroll(record), 250);
                });
                this.checkNewPosts(true);
                return true;
            } catch (error) {
                if (generation !== this.requestGeneration || record.filter !== this.activeFilter) return false;
                const errors = error && error.response && error.response.data && error.response.data.errors;
                if (errors && errors.viewer_marker) {
                    feedSession.safeRemove(this.sessionId);
                    this.sessionId = null;
                    window.location.reload();
                    return false;
                }
                this.restoring = false;
                this.restoreNotice = true;
                return this.startFreshSession(record.filter, true);
            } finally {
                if (generation === this.requestGeneration) this.restoring = false;
            }
        },
        async startFreshSession(filter, scrollTop = true) {
            this.replaceSessionMetadata();
            const generation = ++this.requestGeneration;
            this.items = [];
            this.nextCursor = null;
            this.highWaterToken = null;
            this.hasMore = true;
            this.skippedCount = 0;
            this.currentPageFilter = filter;
            this.error = null;
            this.invalidCursor = false;
            this.loading = false;
            this.loadingGeneration = null;
            if (scrollTop && typeof window !== 'undefined') window.scrollTo(0, 0);

            return this.requestPage(null, true, generation, filter);
        },
        async resetForFilter(filter) {
            this.restoreNotice = false;
            return this.startFreshSession(filter, true);
        },
        async loadMore() {
            if (!this.hasMore || this.loading) return false;

            const loaded = await this.requestPage(this.nextCursor, false, this.requestGeneration, this.activeFilter);
            if (loaded) this.checkNewPosts(false);
            return loaded;
        },
        async restartFeed() {
            return this.resetForFilter(this.activeFilter);
        },
        async retryRequest() {
            return this.requestPage(
                this.lastFailedCursor,
                this.lastFailedReplace,
                this.requestGeneration,
                this.activeFilter,
            );
        },
        async requestPage(cursor, replace, generation, requestedFilter) {
            if (this.loading && this.loadingGeneration === generation) return false;

            this.loading = true;
            this.loadingGeneration = generation;
            this.error = null;
            this.invalidCursor = false;

            const params = { filter: requestedFilter };
            if (cursor) params.cursor = cursor;

            try {
                const response = await axios.get(this.endpoint, {
                    params,
                    headers: { Accept: 'application/json' },
                });

                if (generation !== this.requestGeneration || requestedFilter !== this.activeFilter) {
                    return false;
                }

                const page = response.data;
                if (page.canonical_filter !== requestedFilter || !Array.isArray(page.items)) {
                    throw new Error('Unexpected Feed response');
                }

                this.applyPage(page, replace, false);
                if (replace) this.newPostsAvailable = false;
                this.persistSession();

                return true;
            } catch (error) {
                if (generation !== this.requestGeneration || requestedFilter !== this.activeFilter) {
                    return false;
                }

                this.error = error;
                this.invalidCursor = this.isInvalidCursor(error);
                this.lastFailedCursor = cursor;
                this.lastFailedReplace = replace;

                return false;
            } finally {
                if (generation === this.requestGeneration && this.loadingGeneration === generation) {
                    this.loading = false;
                    this.loadingGeneration = null;
                }
            }
        },
        applyPage(page, replace, restored) {
            const existing = replace ? [] : this.items;
            const seen = new Set(existing.map(item => item.key));
            const incoming = page.items.filter(item => item && typeof item.key === 'string' && !seen.has(item.key) && seen.add(item.key));
            this.items = [...existing, ...incoming];
            this.nextCursor = page.next_cursor || null;
            this.highWaterToken = page.high_water_token || null;
            this.hasMore = Boolean(page.has_more);
            this.skippedCount = restored
                ? Number(page.skipped_count || 0)
                : this.skippedCount + Number(page.skipped_count || 0);
            this.currentPageFilter = page.canonical_filter;
        },
        handlePageHide() {
            this.persistSession(true);
        },
        handleFeedNavigation(event) {
            const anchor = event.target && typeof event.target.closest === 'function'
                ? event.target.closest('a[href]')
                : null;
            if (!anchor) return;
            if (!feedSession.isUnmodifiedSameTabClick(event, anchor)) {
                feedSession.clearPendingReturn();
                return;
            }
            const destination = anchor.href || anchor.getAttribute('href');
            if (!destination || destination === window.location.href) return;

            const saved = this.persistSession(true);
            feedSession.clearPendingReturn();
            if (saved) {
                feedSession.armReturnContext({
                    sessionId: this.sessionId,
                    sourceUrl: window.location.href,
                    destination,
                    viewer: this.viewer,
                });
            }
        },
        handlePageShow(event) {
            if (!event || event.persisted !== true) return;
            const stored = this.sessionId ? feedSession.loadRecord(this.sessionId) : null;
            if (stored && stored.filter === this.activeFilter && stored.viewer_marker === this.viewer) {
                this.restoreFromRecord(stored);
            }
        },
        handleFocus() {
            this.checkNewPosts(false);
        },
        handleVisibilityChange() {
            if (document.visibilityState === 'visible') this.checkNewPosts(false);
        },
        async checkNewPosts(force = false) {
            const visible = typeof document === 'undefined' || document.visibilityState !== 'hidden';
            const filter = feedSession.canonicalFilter(this.activeFilter);
            if (this.continuityDisposed || !this.continuityReady
                || !feedSession.validSessionId(this.sessionId)
                || !feedSession.validViewerMarker(this.viewer)
                || feedSession.viewerMarker() !== this.viewer
                || !filter || this.currentPageFilter !== filter
                || !visible || this.restoring || this.newPostsRequest) return false;
            if (!force && !feedSession.shouldCheckNewPosts(this.lastNewPostCheckAt)) return false;
            if (this.highWaterToken === null && (this.items.length > 0 || this.hasMore)) return false;

            this.lastNewPostCheckAt = Date.now();
            this.persistSession();
            const request = axios.get(this.newPostsEndpoint, {
                params: { filter, high_water_token: this.highWaterToken },
                headers: { Accept: 'application/json' },
            });
            this.newPostsRequest = request;
            try {
                const response = await request;
                if (this.continuityDisposed || filter !== this.activeFilter || response.data.canonical_filter !== filter) return false;
                this.newPostsAvailable = Boolean(response.data.has_new);
                return true;
            } catch (_) {
                return false;
            } finally {
                if (this.newPostsRequest === request) this.newPostsRequest = null;
            }
        },
        startFreshForNewPosts() {
            this.restoreNotice = false;
            return this.startFreshSession(this.activeFilter, true);
        },
        isInvalidCursor(error) {
            if (!error || !error.response || error.response.status !== 422) return false;
            const errors = error.response.data && error.response.data.errors;
            const cursorErrors = errors && errors.cursor;

            return Array.isArray(cursorErrors) && cursorErrors.some(message => {
                return String(message).includes('invalid_feed_cursor');
            });
        },
    },
};
</script>

<style scoped>
.feed-card-enter-active,
.feed-card-leave-active {
    transition: opacity 0.2s ease, transform 0.2s ease;
}

.feed-card-enter,
.feed-card-leave-to {
    opacity: 0;
    transform: translateY(0.5rem);
}
</style>
